import { useNavigate, useLocation } from 'react-router-dom'
import { List, ListItem, ListItemIcon, ListItemText, Tooltip, styled } from '@mui/material';
import { useMediaQuery, useTheme } from '@mui/material';
import { useDispatch } from 'react-redux';
import SpeakerGroup from '@mui/icons-material/SpeakerGroup';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TimelineIcon from '@mui/icons-material/Timeline';
import CategoryIcon from '@mui/icons-material/Category';
import BarChartIcon from '@mui/icons-material/BarChart';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { goTo } from '../../shared/utils';
import { toogleMenu } from '../../redux/navigationSlice';

const StyledList = styled(List)(({ theme }) => ({
    padding: '16px 8px',
}));

const StyledListItem = styled(ListItem, {
    shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'isMenuExpanded',
})(({ theme, isActive, isMenuExpanded }) => ({
    cursor: 'pointer',
    padding: '12px 16px',
    margin: '4px 0',
    borderRadius: '12px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    justifyContent: isMenuExpanded ? 'flex-start' : 'center',
    backgroundColor: isActive ? 'rgba(0, 98, 105, 0.12)' : 'transparent',
    borderLeft: isActive ? '4px solid rgb(0,98,105)' : '4px solid transparent',
    '&:hover': {
        backgroundColor: isActive ? 'rgba(0, 98, 105, 0.18)' : 'rgba(0, 98, 105, 0.08)',
        transform: 'translateX(4px)',
        boxShadow: '0 4px 12px rgba(0, 98, 105, 0.15)',
    },
    '& .MuiListItemIcon-root': {
        color: isActive ? 'rgb(0,98,105)' : '#64748b',
        minWidth: isMenuExpanded ? 48 : 'auto',
        transition: 'all 0.3s ease',
    },
    '& .MuiListItemText-primary': {
        color: isActive ? 'rgb(0,98,105)' : '#334155',
        fontWeight: isActive ? 600 : 500,
        fontSize: '0.95rem',
    },
    '&:hover .MuiListItemIcon-root': {
        color: 'rgb(0,98,105)',
        transform: 'scale(1.1)',
    },
    '&:hover .MuiListItemText-primary': {
        color: 'rgb(0,98,105)',
    },
}));

const StyledListItemIcon = styled(ListItemIcon)({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
});

const ListItems = ({ isMenuExpanded }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useDispatch();

    const handleNavItemClick = (path, event) => {
        // Check if ctrl key (Windows/Linux) or cmd key (Mac) is pressed, or middle-click (button 1)
        if (event.ctrlKey || event.metaKey || event.button === 1) {
            // Open in new tab
            window.open(`/${path}`, '_blank');
            return;
        }
        
        goTo(navigate, path);
        // Close drawer on mobile after navigation
        if (isMobile) {
            dispatch(toogleMenu(false));
        }
    };

    const menuItems = [
        { path: 'accounts', label: 'Contas', icon: <AccountBalanceWalletIcon /> },
        { path: 'groups', label: 'Agrupamentos', icon: <SpeakerGroup /> },        
        { path: 'categories', label: 'Categorias', icon: <CategoryIcon /> },
        { path: 'transactions', label: 'Transações', icon: <MonetizationOnIcon /> },
        { path: 'balance-per-account', label: 'Saldo por conta', icon: <AccountBalanceIcon /> },
        { path: 'balance-per-category', label: 'Saldo por categoria', icon: <BarChartIcon /> },
        { path: 'monthly-balance', label: 'Saldo do mês', icon: <CalendarMonthIcon /> },
        { path: 'future-balance', label: 'Saldo futuro', icon: <TimelineIcon /> },
        { path: 'mydashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    ];

    const isPathActive = (path) => {
        return location.pathname.includes(path);
    };
    
    return (
        <StyledList>
            {menuItems.map((item) => (
                <Tooltip 
                    key={item.path}
                    title={!isMenuExpanded ? item.label : ''} 
                    aria-label={item.label} 
                    placement="right"
                >
                    <StyledListItem 
                        component="li" 
                        onClick={(event) => handleNavItemClick(item.path, event)}
                        onAuxClick={(event) => {
                            event.preventDefault();
                            handleNavItemClick(item.path, event);
                        }}
                        isActive={isPathActive(item.path)}
                        isMenuExpanded={isMenuExpanded}
                    >
                        <StyledListItemIcon>
                            {item.icon}
                        </StyledListItemIcon>
                        {isMenuExpanded && <ListItemText primary={item.label} />}
                    </StyledListItem>
                </Tooltip>
            ))}
        </StyledList>
    );
};

export default ListItems;